/**
 * @fileoverview Badge Registry - Complete Configuration
 *
 * Central registry of all badge configurations including colors, icons,
 * copy, animations, and accessibility properties.
 *
 * @author LOCI Development Team
 * @since 1.0.0
 */

import type { BadgeRegistry, BadgeConfig, BadgeType } from './badge';

/**
 * Complete badge configuration registry
 * Defines all badge types with their complete visual and behavioral specifications
 */
export const BADGE_REGISTRY: BadgeRegistry = {
  'live-now': {
    type: 'live-now',
    appearance: {
      variant: 'accent',
      size: 'standard',
      style: 'filled',
      borderRadius: 'rounded-full',
      typography: 'font-semibold text-xs',
      elevation: 'shadow-md',
    },
    colors: {
      background: '#10b981', // green-500
      foreground: '#f0fdf4', // green-50
      border: '#059669', // green-600
      hover: '#059669',
      focus: '#047857',
      contrast: 7.2, // Excellent contrast
    },
    icon: {
      name: 'Radio',
      size: 16,
      source: 'lucide',
      alt: 'Live broadcast',
      weight: 'regular',
    },
    copy: {
      primary: 'Live Now',
      short: 'Live',
      aria: 'Event is currently in progress',
      tooltip: 'This event is happening right now!',
      i18n: 'badge.liveNow',
    },
    animation: {
      pulse: true,
      fadeIn: true,
      bounce: false,
      duration: 2000,
      respectMotionPreference: true,
    },
    a11y: {
      role: 'status',
      'aria-label': 'Event is currently in progress',
      'aria-live': 'assertive',
    },
    priority: 10, // Highest priority
  },

  'sold-out': {
    type: 'sold-out',
    appearance: {
      variant: 'warning',
      size: 'standard',
      style: 'filled',
      borderRadius: 'rounded-md',
      typography: 'font-semibold text-xs',
      elevation: 'shadow-sm',
    },
    colors: {
      background: '#ef4444', // red-500
      foreground: '#fef2f2', // red-50
      border: '#dc2626', // red-600
      hover: '#dc2626',
      focus: '#b91c1c',
      contrast: 6.8,
    },
    icon: {
      name: 'X',
      size: 16,
      source: 'lucide',
      alt: 'Sold out',
      weight: 'bold',
    },
    copy: {
      primary: 'Sold Out',
      short: 'Full',
      aria: 'Event has reached capacity',
      tooltip: 'This event has sold out',
      i18n: 'badge.soldOut',
    },
    animation: {
      pulse: false,
      fadeIn: true,
      bounce: false,
      duration: 300,
      respectMotionPreference: true,
    },
    a11y: {
      role: 'status',
      'aria-label': 'Event has reached capacity',
      'aria-live': 'polite',
    },
    priority: 9,
  },

  'almost-full': {
    type: 'almost-full',
    appearance: {
      variant: 'warning',
      size: 'standard',
      style: 'filled',
      borderRadius: 'rounded-md',
      typography: 'font-semibold text-xs',
      elevation: 'shadow-sm',
    },
    colors: {
      background: '#f59e0b', // amber-500
      foreground: '#fffbeb', // amber-50
      border: '#d97706', // amber-600
      hover: '#d97706',
      focus: '#b45309',
      contrast: 5.5,
    },
    icon: {
      name: 'Users',
      size: 16,
      source: 'lucide',
      alt: 'Almost full',
      weight: 'regular',
    },
    copy: {
      primary: 'Almost Full',
      short: 'Filling',
      aria: 'Limited spots remaining',
      tooltip: 'Only a few tickets left!',
      i18n: 'badge.almostFull',
    },
    animation: {
      pulse: false,
      fadeIn: true,
      bounce: true,
      duration: 300,
      respectMotionPreference: true,
    },
    a11y: {
      role: 'status',
      'aria-label': 'Limited spots remaining',
      'aria-live': 'polite',
    },
    priority: 8,
  },

  canceled: {
    type: 'canceled',
    appearance: {
      variant: 'secondary',
      size: 'standard',
      style: 'filled',
      borderRadius: 'rounded-md',
      typography: 'font-semibold text-xs',
      elevation: 'shadow-sm',
    },
    colors: {
      background: '#6b7280', // gray-500
      foreground: '#f9fafb', // gray-50
      border: '#4b5563', // gray-600
      hover: '#4b5563',
      focus: '#374151',
      contrast: 5.2,
    },
    icon: {
      name: 'XCircle',
      size: 16,
      source: 'lucide',
      alt: 'Canceled',
      weight: 'regular',
    },
    copy: {
      primary: 'Canceled',
      short: 'Canceled',
      aria: 'Event has been canceled',
      tooltip: 'This event has been canceled',
      i18n: 'badge.canceled',
    },
    animation: {
      pulse: false,
      fadeIn: true,
      bounce: false,
      duration: 300,
      respectMotionPreference: true,
    },
    a11y: {
      role: 'status',
      'aria-label': 'Event has been canceled',
      'aria-live': 'assertive',
    },
    priority: 10, // Same as live-now
  },

  featured: {
    type: 'featured',
    appearance: {
      variant: 'primary',
      size: 'standard',
      style: 'filled',
      borderRadius: 'rounded-md',
      typography: 'font-semibold text-xs',
      elevation: 'shadow-md',
    },
    colors: {
      background: '#8b5cf6', // violet-500
      foreground: '#faf5ff', // violet-50
      border: '#7c3aed', // violet-600
      hover: '#7c3aed',
      focus: '#6d28d9',
      contrast: 6.1,
    },
    icon: {
      name: 'Star',
      size: 16,
      source: 'lucide',
      alt: 'Featured',
      weight: 'bold',
    },
    copy: {
      primary: 'Featured',
      short: '★',
      aria: 'Featured event',
      tooltip: 'This is a featured event',
      i18n: 'badge.featured',
    },
    animation: {
      pulse: false,
      fadeIn: true,
      bounce: false,
      duration: 300,
      respectMotionPreference: true,
    },
    a11y: {
      role: 'img',
      'aria-label': 'Featured event',
    },
    priority: 6,
  },

  new: {
    type: 'new',
    appearance: {
      variant: 'success',
      size: 'compact',
      style: 'filled',
      borderRadius: 'rounded-full',
      typography: 'font-bold text-xs',
      elevation: 'shadow-sm',
    },
    colors: {
      background: '#22c55e', // green-500
      foreground: '#f0fdf4', // green-50
      border: '#16a34a', // green-600
      hover: '#16a34a',
      focus: '#15803d',
      contrast: 6.9,
    },
    icon: {
      name: 'Sparkles',
      size: 12,
      source: 'lucide',
      alt: 'New',
      weight: 'regular',
    },
    copy: {
      primary: 'New',
      short: 'New',
      aria: 'Recently added event',
      tooltip: 'Recently added',
      i18n: 'badge.new',
    },
    animation: {
      pulse: false,
      fadeIn: true,
      bounce: true,
      duration: 300,
      respectMotionPreference: true,
    },
    a11y: {
      role: 'img',
      'aria-label': 'Recently added event',
    },
    priority: 4,
  },

  'near-you': {
    type: 'near-you',
    appearance: {
      variant: 'accent',
      size: 'compact',
      style: 'filled',
      borderRadius: 'rounded-full',
      typography: 'font-semibold text-xs',
      elevation: 'shadow-sm',
    },
    colors: {
      background: '#3b82f6', // blue-500
      foreground: '#eff6ff', // blue-50
      border: '#2563eb', // blue-600
      hover: '#2563eb',
      focus: '#1d4ed8',
      contrast: 5.8,
    },
    icon: {
      name: 'MapPin',
      size: 12,
      source: 'lucide',
      alt: 'Near you',
      weight: 'regular',
    },
    copy: {
      primary: 'Near You',
      short: '📍',
      aria: 'Event near your location',
      tooltip: 'This event is near your location',
      i18n: 'badge.nearYou',
    },
    animation: {
      pulse: false,
      fadeIn: true,
      bounce: false,
      duration: 300,
      respectMotionPreference: true,
    },
    a11y: {
      role: 'img',
      'aria-label': 'Event near your location',
    },
    priority: 5,
  },

  online: {
    type: 'online',
    appearance: {
      variant: 'secondary',
      size: 'compact',
      style: 'outlined',
      borderRadius: 'rounded-md',
      typography: 'font-medium text-xs',
      elevation: 'shadow-none',
    },
    colors: {
      background: '#f0f9ff', // sky-50
      foreground: '#0369a1', // sky-700
      border: '#0ea5e9', // sky-500
      hover: '#e0f2fe',
      focus: '#bae6fd',
      contrast: 5.1,
    },
    icon: {
      name: 'Monitor',
      size: 12,
      source: 'lucide',
      alt: 'Online',
      weight: 'regular',
    },
    copy: {
      primary: 'Online',
      short: '🌐',
      aria: 'Virtual event',
      tooltip: 'Join this event online',
      i18n: 'badge.online',
    },
    animation: {
      pulse: false,
      fadeIn: true,
      bounce: false,
      duration: 300,
      respectMotionPreference: true,
    },
    a11y: {
      role: 'img',
      'aria-label': 'Virtual event',
    },
    priority: 3,
  },

  registered: {
    type: 'registered',
    appearance: {
      variant: 'success',
      size: 'standard',
      style: 'filled',
      borderRadius: 'rounded-md',
      typography: 'font-semibold text-xs',
      elevation: 'shadow-sm',
    },
    colors: {
      background: '#10b981', // emerald-500
      foreground: '#ecfdf5', // emerald-50
      border: '#059669', // emerald-600
      hover: '#059669',
      focus: '#047857',
      contrast: 6.5,
    },
    icon: {
      name: 'Check',
      size: 16,
      source: 'lucide',
      alt: 'Registered',
      weight: 'bold',
    },
    copy: {
      primary: 'Registered',
      short: '✓',
      aria: 'You are registered for this event',
      tooltip: "You're going to this event",
      i18n: 'badge.registered',
    },
    animation: {
      pulse: false,
      fadeIn: true,
      bounce: false,
      duration: 300,
      respectMotionPreference: true,
    },
    a11y: {
      role: 'status',
      'aria-label': 'You are registered for this event',
      'aria-live': 'polite',
    },
    priority: 7,
  },

  recurring: {
    type: 'recurring',
    appearance: {
      variant: 'secondary',
      size: 'compact',
      style: 'outlined',
      borderRadius: 'rounded-md',
      typography: 'font-medium text-xs',
      elevation: 'shadow-none',
    },
    colors: {
      background: '#fef3c7', // amber-100
      foreground: '#78350f', // amber-900
      border: '#f59e0b', // amber-500
      hover: '#fef3c7',
      focus: '#fde68a',
      contrast: 8.1,
    },
    icon: {
      name: 'RotateCcw',
      size: 12,
      source: 'lucide',
      alt: 'Recurring',
      weight: 'regular',
    },
    copy: {
      primary: 'Recurring',
      short: '↻',
      aria: 'This event repeats',
      tooltip: 'This event happens regularly',
      i18n: 'badge.recurring',
    },
    animation: {
      pulse: false,
      fadeIn: true,
      bounce: false,
      duration: 300,
      respectMotionPreference: true,
    },
    a11y: {
      role: 'img',
      'aria-label': 'This event repeats',
    },
    priority: 2,
  },
};

/**
 * Get badge configuration by type
 */
export function getBadgeConfig(type: BadgeType): BadgeConfig | undefined {
  return BADGE_REGISTRY[type];
}

/**
 * Get all badge types sorted by priority
 */
const SORTED_BADGE_TYPES_BY_PRIORITY: BadgeType[] = Object.values(BADGE_REGISTRY)
  .sort((a, b) => {
    if (b.priority !== a.priority) {
      return b.priority - a.priority;
    }
    return a.type.localeCompare(b.type); // Stable sort for tie-breaking
  })
  .map((config) => config.type);

/**
 * Get all badge types sorted by priority
 */
export function getBadgeTypesByPriority(): BadgeType[] {
  return SORTED_BADGE_TYPES_BY_PRIORITY;
}

/* Badge type import for convenience */
export type { BadgeType } from './badge';
